import { useId } from "react";
import { Info } from "lucide-react";

import { Tooltip } from "./Tooltip";

interface InfoTipProps {
  /** The explanation. Plain text — it is announced as well as shown. */
  text: string;
  /** Accessible name for the trigger itself, e.g. "About this field". */
  label: string;
  className?: string;
}

/**
 * The little "i" beside a label, and the sentence it holds.
 *
 * Now a trigger and nothing else: the positioning, the viewport clamping and
 * the four ways a tooltip has to be able to close all live in `Tooltip`, which
 * was lifted out of this file when a second control needed the same behaviour
 * (a disabled menu item explaining why it is disabled). One implementation, two
 * triggers — rather than two implementations that would eventually disagree
 * about what happens at the bottom of a phone screen.
 *
 * The button stays a real `<button>` so the tip is reachable by Tab and
 * dismissible by Escape; `Tooltip` listens for focus on its wrapper, which hears
 * this button because React's onFocus is focusin and therefore bubbles.
 */
export function InfoTip({ text, label, className = "" }: InfoTipProps) {
  const id = useId();

  return (
    <Tooltip text={text} id={id} className="inline-flex">
      <button
        type="button"
        aria-label={label}
        aria-describedby={id}
        className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-mist transition-colors hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-copper/60 ${className}`}
      >
        <Info className="h-3.5 w-3.5" />
      </button>
    </Tooltip>
  );
}
